import * as turf from "@turf/turf"
import type { Geometry } from "geojson"
import { LngLat, type MapEventType, MapTouchEvent } from "mapbox-gl"
import { useCallback, useEffect, useRef, useState } from "react"
import { type MapRef, useMap } from "react-map-gl/mapbox"

import { BOUNDS_PADDING } from "../constants"
import type { MapEventHandler } from "../typings"

export const useCurrentMap = () => useMap().current

export const useMapEvent = <TMapEvent extends MapEventType>(
	map: MapRef | undefined,
	type: TMapEvent,
	handler: MapEventHandler<TMapEvent>,
) => {
	useEffect(() => {
		if (!map) return

		map.on(type, handler as MapEventHandler<TMapEvent>)

		return () => {
			map.off(type, handler as MapEventHandler<TMapEvent>)
		}
	}, [handler, map, type])
}

export const useCurrentMapEvent = <TMapEvent extends MapEventType>(
	type: TMapEvent,
	handler: MapEventHandler<TMapEvent>,
) => {
	const { current } = useMap()

	useMapEvent(current, type, handler)
}

export const useViewGeometryOnMap = () => {
	const { current: map } = useMap()

	return useCallback(
		(geometry: Geometry) => {
			// @ts-expect-error REASON: bbox provides extra coords for 3d geojson
			map?.fitBounds(turf.bbox(geometry), {
				padding: BOUNDS_PADDING,
				animate: false,
			})
		},
		[map],
	)
}

export const useMapCenter = (map: MapRef | undefined) => {
	const [center, setCenter] = useState<LngLat>(() => map!.getCenter())

	const handleMapMove = useCallback(() => {
		setCenter(map!.getCenter())
	}, [map])

	useCurrentMapEvent("move", handleMapMove)

	return center
}

export const useMapZoom = (map: MapRef | undefined) => {
	const [zoom, setZoom] = useState<number>(() => map!.getZoom())

	const handleMapMove = useCallback(() => {
		setZoom(map!.getZoom())
	}, [map])

	useCurrentMapEvent("zoom", handleMapMove)

	return zoom
}

export const useMapBearing = (map: MapRef | undefined) => {
	const [bearing, setBearing] = useState<number>(() => map!.getBearing())

	const handleMapMove = useCallback(() => {
		setBearing(map!.getBearing())
	}, [map])

	useCurrentMapEvent("rotate", handleMapMove)

	return bearing
}

export const useMapLoad = () => {
	const [loaded, setLoaded] = useState(true)
	const [styleLoaded, setStyleLoaded] = useState(true)

	const handleLoad = useCallback(() => {
		setLoaded(true)
	}, [])

	useCurrentMapEvent("load", handleLoad)

	const handleStyleLoad = useCallback(() => {
		setStyleLoaded(true)
	}, [])

	useCurrentMapEvent("style.load", handleStyleLoad)

	const handleStyleLoading = useCallback(() => {
		setStyleLoaded(false)
	}, [])

	useCurrentMapEvent("styledataloading", handleStyleLoading)

	return [loaded, styleLoaded] as const
}

export const useMapLongPress = (map: MapRef | undefined, handler: MapEventHandler<"touchstart">) => {
	const timer = useRef<number | null>(null)

	const handleTouchStart = useCallback(
		(e: MapTouchEvent) => {
			if (e.originalEvent.touches.length > 1) return

			timer.current = window.setTimeout(() => {
				handler(e)
			}, 500)
		},
		[handler],
	)

	const handleTouchEnd = useCallback((_e: MapTouchEvent) => {
		if (!timer.current) return
		clearTimeout(timer.current)
	}, [])

	const handleTouchMove = useCallback((_e: MapTouchEvent) => {
		if (!timer.current) return
		clearTimeout(timer.current)
	}, [])

	useEffect(() => {
		if (!map) return

		map.on("touchstart", handleTouchStart)
		map.on("touchend", handleTouchEnd)
		map.on("touchmove", handleTouchMove)

		return () => {
			map.off("touchstart", handleTouchStart)
			map.off("touchend", handleTouchEnd)
			map.off("touchmove", handleTouchMove)
		}
	}, [map, handleTouchStart, handleTouchEnd, handleTouchMove])
}

export const useCurrentMapLongPress = (handler: MapEventHandler<"touchstart">) => {
	const map = useCurrentMap()
	useMapLongPress(map, handler)
}
